import React from "react";

function StatCard({ label, value, sublabel }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
      {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
    </div>
  );
}

export function HeroStrip({ completedCount, streaks }) {
  const currentLabel =
    streaks.current === 0 && streaks.longest > 0
      ? "Start a new week"
      : `${streaks.current} ${streaks.current === 1 ? "week" : "weeks"}`;
  const longestLabel = `${streaks.longest} ${streaks.longest === 1 ? "week" : "weeks"}`;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <StatCard label="Sessions completed" value={completedCount} />
      <StatCard label="This week's streak" value={currentLabel} />
      <StatCard label="Longest streak" value={longestLabel} />
    </div>
  );
}

const HEATMAP_COLOR = (count) => {
  if (count === 0) return "bg-gray-100";
  if (count === 1) return "bg-accent/30";
  if (count === 2) return "bg-accent/60";
  return "bg-accent";
};

export function WeekHeatmap({ cells }) {
  if (!cells || cells.length === 0) return null;
  const formatRange = (start) => {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${fmt(start)} – ${fmt(end)}`;
  };
  return (
    <div className="bg-white rounded-lg shadow-md p-5 border border-gray-100 mb-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last 26 weeks</p>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <span>Less</span>
          <span className={`inline-block w-3 h-3 rounded-sm ${HEATMAP_COLOR(0)}`} />
          <span className={`inline-block w-3 h-3 rounded-sm ${HEATMAP_COLOR(1)}`} />
          <span className={`inline-block w-3 h-3 rounded-sm ${HEATMAP_COLOR(2)}`} />
          <span className={`inline-block w-3 h-3 rounded-sm ${HEATMAP_COLOR(3)}`} />
          <span>More</span>
        </div>
      </div>
      <div className="flex gap-1 flex-wrap">
        {cells.map((cell) => (
          <div
            key={cell.weekKey}
            className={`w-5 h-5 rounded-sm ${HEATMAP_COLOR(cell.count)}`}
            title={`${formatRange(cell.weekStart)} — ${cell.count} ${cell.count === 1 ? "session" : "sessions"}`}
          />
        ))}
      </div>
    </div>
  );
}

const TIME_OF_DAY_LABEL = {
  morning: "Morning user",
  afternoon: "Afternoon user",
  evening: "Evening user",
  late: "Late-night user",
};

export function DetailStatsGrid({
  bestLift,
  avgLift,
  totalMinutes,
  timeOfDay,
  mostUsedType,
  weeksPracticed,
  unfinishedCount,
  getDisplayName,
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
      {bestLift && (
        <StatCard
          label="Best mood lift"
          value={`+${bestLift.delta}`}
          sublabel={new Date(bestLift.date).toLocaleDateString()}
        />
      )}
      {avgLift !== null && (
        <StatCard
          label="Avg lift after a session"
          value={`${avgLift >= 0 ? "+" : ""}${avgLift.toFixed(1)}`}
        />
      )}
      {totalMinutes > 0 && (
        <StatCard
          label="Total minutes"
          value={totalMinutes}
          sublabel="bilateral stimulation"
        />
      )}
      {timeOfDay && (
        <StatCard label="When you use it" value={TIME_OF_DAY_LABEL[timeOfDay.id]} />
      )}
      {mostUsedType && (
        <StatCard label="Most used" value={getDisplayName(mostUsedType)} />
      )}
      {weeksPracticed > 0 && (
        <StatCard
          label="Weeks of practice"
          value={weeksPracticed}
          sublabel={weeksPracticed === 1 ? "and counting" : "lifetime"}
        />
      )}
      {unfinishedCount > 0 && (
        <StatCard
          label="Sessions started"
          value={unfinishedCount}
          sublabel="no judgment — life happens"
        />
      )}
    </div>
  );
}

export function MilestonesList({ milestones }) {
  if (!milestones || milestones.length === 0) return null;
  return (
    <div className="bg-white rounded-lg shadow-md p-5 border border-gray-100 mb-6">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
        Milestones
      </p>
      <ul className="flex flex-wrap gap-2">
        {milestones.map((m) => (
          <li
            key={m.id}
            className="inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1.5 rounded-full text-sm font-medium"
          >
            <span>🏅</span>
            <span>{m.label}</span>
            {m.dateEarned && (
              <span className="text-xs text-accent/70">
                {new Date(m.dateEarned).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
